/**
 * PullPress Premium Publishing UI — Design Tokens
 *
 * Single canonical source of truth. Existing token keys stay stable so
 * route code can keep using semantic Tailwind aliases while the product
 * moves toward a low-glare editorial operating system.
 */

// ─── Colors ────────────────────────────────────────────────────

export const colors = {
  // Surface — low-glare editorial canvas
  paper:    "#F6F0E5",
  paper2:   "#EDE4D6",
  paper3:   "#D7CBB8",
  paperRaised: "#FFFDF8",
  margin:   "#FBF7EF",
  tape:     "#C8B187",

  // Ink — system text tiers
  ink:    "#191814",
  ink2:   "#34312A",
  ink3:   "#6C665B",
  ink4:   "#9A9284",

  // Primary action — verdigris with aged copper support
  walnut:     "#23534A",
  walnutDeep: "#143A35",
  walnutSoft: "#B87945",
  walnutTint: "#DDE8E3",

  // Proof / verify / flag states
  proof:     "#526873",
  proofSoft: "#8EA0A0",
  proofTint: "#E7ECEA",
  verify:     "#466F56",
  verifySoft: "#86A584",
  verifyTint: "#E7EFE7",
  flag:     "#8B3E36",
  flagTint: "#F1E0DB",

  // Hairlines + selection
  rule:        "#D8CDBE",
  ruleStrong:  "#B9AA97",
  ruleFaint:   "#EAE1D4",
  selection:   "#D9BE7C",
  highlight:   "#EFE1B8",
} as const;

// ─── Typography ────────────────────────────────────────────────

export const fonts = {
  display: "'Source Serif 4', 'Iowan Old Style', 'New York', Georgia, serif",
  ui:      "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif",
  text:    "'Source Serif 4', 'Iowan Old Style', 'New York', Georgia, serif",
  mono:    "'IBM Plex Mono', 'JetBrains Mono', ui-monospace, Menlo, monospace",
} as const;

// Modular scale, base 16, ratio 1.333 (perfect fourth)
export const fontSize = {
  t12: "0.6875rem",
  t13: "0.8125rem",
  t14: "0.875rem",
  t16: "1rem",      // 16px    — body UI
  t18: "1.125rem",  // 18px    — running serif
  t21: "1.25rem",   // 20px    — lead / blockquote
  t28: "1.5rem",    // 24px    — H4
  t37: "1.95rem",   // 31px    — H3
  t49: "2.55rem",   // 41px    — H2
  t66: "3.35rem",   // 54px    — H1 / chapter
  t88: "4.25rem",
} as const;

export const tracking = {
  tight:   "0em",
  snug:    "0em",
  normal:  "0em",
  wide:    "0.04em",
  eyebrow: "0.16em",
} as const;

export const leading = {
  tight: "1.05",
  snug:  "1.18",
  ui:    "1.38",
  read:  "1.58",
} as const;

// ─── Spacing & layout ──────────────────────────────────────────

export const spacing = {
  s1:  "4px",
  s2:  "8px",
  s3:  "12px",
  s4:  "16px",
  s5:  "24px",  // grid gutter
  s6:  "32px",
  s7:  "48px",
  s8:  "64px",
  s9:  "96px",
  s10: "144px",
} as const;

export const layout = {
  gridGutter:    "24px",
  gridColNominal:"88px",   // on 1440 canvas
  signatureRatio:"6/9",    // print signature aspect
  whitespaceTarget: 0.46,
  readerMeasure: "72ch",
  editorMeasure: "78ch",
  touchTarget: "44px",
  safeTop: "env(safe-area-inset-top, 0px)",
  safeBottom: "env(safe-area-inset-bottom, 0px)",
} as const;

export const radius = {
  r0:    "0px",
  r1:    "12px",
  r2:    "18px",
  r3:    "28px",
  pill:  "999px",   // proof-state badges only
} as const;

// ─── Elevation ────────────────────────────────────────────────

export const shadow = {
  s0:     "none",
  s1:     "0 1px 1px rgba(25, 24, 20, 0.03), 0 12px 32px rgba(25, 24, 20, 0.04)",
  s2:     "0 2px 4px rgba(25, 24, 20, 0.04), 0 20px 54px rgba(25, 24, 20, 0.07)",
  s3:     "0 6px 18px rgba(25, 24, 20, 0.07), 0 36px 88px rgba(25, 24, 20, 0.10)",
  press:  "inset 0 1px 2px rgba(0, 0, 0, 0.12)",
  page:   "0 16px 42px rgba(25, 24, 20, 0.16), 0 1px 3px rgba(25, 24, 20, 0.08)",
} as const;

// ─── Motion ────────────────────────────────────────────────────

export const motion = {
  easeOut:   "cubic-bezier(.2, .7, .2, 1)",
  easeInOut: "cubic-bezier(.6, 0, .2, 1)",
  dur1: "120ms", // hover/press feedback
  dur2: "200ms", // default transition
  dur3: "320ms", // modal / lane reflow
} as const;

// ─── Bundled export for Tailwind config consumption ────────────

export const tokens = {
  colors,
  fonts,
  fontSize,
  tracking,
  leading,
  spacing,
  layout,
  radius,
  shadow,
  motion,
} as const;

export default tokens;
